import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MailPlus, Send } from "lucide-react";

export default function EditionsTab() {
  const [editions, setEditions] = useState([]);
  const [published, setPublished] = useState([]);
  const [selected, setSelected] = useState("");
  const [intro, setIntro] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api.get("/admin/newsletter-editions").then((r) => setEditions(r.data)).catch(() => {});
    api.get("/admin/articles").then((r) => setPublished(r.data.filter((a) => a.status === "PUBLISHED"))).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/admin/newsletter-editions", { article_id: selected, intro });
      toast.success("Newsletter edition drafted — awaiting send approval");
      setIntro("");
      setSelected("");
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const send = async (ed) => {
    if (!window.confirm(`Send "${ed.article_title}" (v${ed.article_version}) to every active subscriber? This cannot be undone.`)) return;
    setBusy(true);
    setError("");
    try {
      const r = await api.post(`/admin/newsletter-editions/${ed.edition_id}/send`, { confirm: true });
      toast.success(`Edition sent to ${r.data.sent_count} active subscriber(s)`);
      load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="editions-tab">
      <h2 className="font-serif text-2xl text-hussar-ink mb-2">Newsletter Editions</h2>
      <p className="text-hussar-muted text-xs font-mono uppercase tracking-[0.15em] mb-8">
        Publishing an article never emails subscribers — sending is a separate explicit approval
      </p>

      <div className="border border-white/5 bg-hussar-card p-6 mb-10">
        <div className="metadata-label !text-[10px] mb-4">Draft a Newsletter Edition</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="edition-article-select" className="field-input">
            <option value="">Select a published edition…</option>
            {published.map((a) => (
              <option key={a.article_id} value={a.article_id}>{a.title} — v{a.current_version}</option>
            ))}
          </select>
          <input value={intro} onChange={(e) => setIntro(e.target.value)} data-testid="edition-intro-input" className="field-input" placeholder="Optional one-line editorial intro" />
        </div>
        <button onClick={create} disabled={busy || !selected} data-testid="edition-create-btn" className="btn-gold !px-5 !py-2.5 disabled:opacity-40">
          <MailPlus size={13} /> Create Draft Edition
        </button>
        {error && <p data-testid="edition-error" className="text-red-400 text-xs font-mono mt-4">{error}</p>}
      </div>

      {editions.length === 0 ? (
        <div className="border border-white/5 bg-hussar-card p-12 text-center text-hussar-muted font-mono text-xs uppercase tracking-widest">
          No newsletter editions yet.
        </div>
      ) : (
        <div className="border border-white/5">
          {editions.map((ed) => (
            <div key={ed.edition_id} className="grid grid-cols-12 gap-4 items-center px-6 py-5 border-b border-white/5 bg-hussar-card" data-testid={`edition-row-${ed.edition_id}`}>
              <div className="col-span-6">
                <div className="text-hussar-ink font-serif">{ed.article_title}</div>
                <div className="text-hussar-muted text-xs font-mono mt-1">article v{ed.article_version} · {new Date(ed.created_at).toLocaleDateString("en-GB")}</div>
              </div>
              <div className="col-span-2">
                <span className={`font-mono text-[10px] uppercase tracking-[0.18em] border px-2.5 py-1 ${ed.status === "SENT" ? "text-emerald-400 border-emerald-400/30" : "text-hussar-slate border-white/15"}`}>
                  {ed.status}
                </span>
              </div>
              <div className="col-span-2 text-hussar-muted text-xs font-mono">
                {ed.status === "SENT" ? `${ed.sent_count} sent` : "not sent"}
              </div>
              <div className="col-span-2 text-right">
                {ed.status !== "SENT" && (
                  <button onClick={() => send(ed)} disabled={busy} data-testid={`edition-send-${ed.edition_id}`} className="btn-gold !px-4 !py-2 disabled:opacity-40">
                    <Send size={12} /> Approve & Send
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
